import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, Clock } from 'lucide-react';

export default function MarginOfSafety({ user, onLogout }) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen p-4 lg:p-8">
      <Navigation user={user} onLogout={onLogout} />
      <div className="max-w-4xl mx-auto">
        
        <Button 
          variant="ghost" 
          onClick={() => navigate('/learn')} 
          className="mb-6 dark:text-gray-300"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Learn Home
        </Button>

        <Card className="glass p-8 mb-6 dark:bg-gray-800">
          <div className="flex items-center space-x-2 mb-4">
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
              Advanced
            </span>
            <span className="flex items-center text-sm text-gray-500 dark:text-gray-400">
              <Clock className="w-4 h-4 mr-1" />
              12 min read
            </span>
          </div>

          <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100 mb-4">
            🛡️ Margin of Safety & Long-term Thinking
          </h1>

          <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
            Learn to buy with a margin of safety and think like a long-term investor
          </p>

          <div className="prose dark:prose-invert max-w-none space-y-6 text-gray-700 dark:text-gray-300">
            
            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">The Most Important Concept in Investing</h2>
              <p className="mb-4">
                Benjamin Graham, Warren Buffett's mentor, wrote: <em>"The margin of safety is the cornerstone of investment success."</em>
              </p>
              <p className="mb-4">
                Think of it this way: When engineers build a bridge for 10,000 kg capacity, they actually build it to withstand 30,000 kg. 
                That extra 20,000 kg is the <strong>margin of safety</strong> - protection against unexpected stress.
              </p>
              <p className="mb-4">
                In investing, margin of safety means: <strong>Never pay full price.</strong> Always demand a discount to protect yourself 
                from calculation errors, bad luck, or unforeseen problems.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">How Margin of Safety Works</h2>
              
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg mb-4">
                <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">Simple Formula:</p>
                <p className="text-lg font-mono font-bold text-center py-2">
                  Margin of Safety = (Intrinsic Value - Market Price) / Intrinsic Value × 100
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm border dark:border-gray-600 mb-4">
                  <thead className="bg-gray-100 dark:bg-gray-700">
                    <tr>
                      <th className="p-3 text-left">Scenario</th>
                      <th className="p-3 text-right">Intrinsic Value</th>
                      <th className="p-3 text-right">Market Price</th>
                      <th className="p-3 text-right">Margin of Safety</th>
                      <th className="p-3 text-center">Decision</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t dark:border-gray-600 bg-green-50 dark:bg-green-900/10">
                      <td className="p-3">Excellent Deal</td>
                      <td className="p-3 text-right">₹3,000</td>
                      <td className="p-3 text-right">₹2,000</td>
                      <td className="p-3 text-right font-bold text-green-600">33%</td>
                      <td className="p-3 text-center">✅ Buy!</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600 bg-green-50 dark:bg-green-900/5">
                      <td className="p-3">Good Deal</td>
                      <td className="p-3 text-right">₹3,000</td>
                      <td className="p-3 text-right">₹2,400</td>
                      <td className="p-3 text-right font-bold text-green-600">20%</td>
                      <td className="p-3 text-center">✅ Buy</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600">
                      <td className="p-3">Fair Value</td>
                      <td className="p-3 text-right">₹3,000</td>
                      <td className="p-3 text-right">₹2,900</td>
                      <td className="p-3 text-right font-bold text-yellow-600">3%</td>
                      <td className="p-3 text-center">⚠️ Wait</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600 bg-red-50 dark:bg-red-900/10">
                      <td className="p-3">Overvalued</td>
                      <td className="p-3 text-right">₹3,000</td>
                      <td className="p-3 text-right">₹3,500</td>
                      <td className="p-3 text-right font-bold text-red-600">-17%</td>
                      <td className="p-3 text-center">❌ Avoid</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded border-l-4 border-yellow-500">
                <p className="text-sm">
                  <strong>Graham's Rule:</strong> Minimum 20-30% margin of safety for stocks. For quality companies, 15-20% may suffice. 
                  For risky companies, demand 40-50% discount!
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">Real Indian Example: HDFC Bank in 2020</h2>
              
              <div className="space-y-3">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded">
                  <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">Before COVID Crash (Feb 2020):</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>Intrinsic Value: ₹1,400</div>
                    <div>Market Price: ₹1,500</div>
                    <div className="col-span-2 text-red-600 font-bold">Margin of Safety: -7% (Overvalued)</div>
                  </div>
                  <p className="text-sm mt-2">Smart investors waited. No margin of safety!</p>
                </div>

                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded border-2 border-green-500">
                  <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">During Panic (March 2020):</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>Intrinsic Value: ₹1,400 (unchanged)</div>
                    <div>Market Price: ₹900</div>
                    <div className="col-span-2 text-green-600 font-bold">Margin of Safety: 36% (Excellent!)</div>
                  </div>
                  <p className="text-sm mt-2">Warren Buffett disciples bought aggressively. Huge discount!</p>
                </div>

                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded">
                  <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">6 Months Later (Sept 2020):</p>
                  <div className="text-sm">
                    <p>Market Price recovered to ₹1,300</p>
                    <p className="font-bold text-purple-600 mt-1">Return: 44% gain in 6 months + margin of safety protected downside</p>
                  </div>
                </div>
              </div>

              <p className="text-sm mt-3 italic">
                <strong>Lesson:</strong> Margin of safety gave investors the confidence to buy during panic. 
                Even if intrinsic value estimate was wrong by 20%, they still made money!
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">Why Margin of Safety Protects You</h2>
              
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded">
                  <h4 className="font-bold text-gray-800 dark:text-gray-100 mb-2">1. Protection from Valuation Errors</h4>
                  <p className="text-sm">
                    You calculated intrinsic value as ₹3,000 but you were wrong - it's actually ₹2,500. 
                    If you bought at ₹2,000 (33% margin), you're still safe. If you bought at ₹2,900, you lose money!
                  </p>
                </div>

                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded">
                  <h4 className="font-bold text-gray-800 dark:text-gray-100 mb-2">2. Protection from Bad Luck</h4>
                  <p className="text-sm">
                    Company faces unexpected challenges (new regulation, key person leaves, competitive threat). 
                    With margin of safety, you have cushion to absorb the blow.
                  </p>
                </div>

                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded">
                  <h4 className="font-bold text-gray-800 dark:text-gray-100 mb-2">3. Protection from Market Irrationality</h4>
                  <p className="text-sm">
                    Markets can stay irrational longer than you expect. With margin of safety, even if price falls further temporarily, 
                    you have breathing room and patience to wait for value realization.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">Long-term Thinking: The Rip Van Winkle Strategy</h2>
              <p className="mb-4">
                Warren Buffett once said: <em>"If you aren't thinking about owning a stock for 10 years, don't even think about owning it for 10 minutes."</em>
              </p>
              <p className="mb-4">
                Imagine you bought TCS in 2010 and went into a coma (like Rip Van Winkle) until 2024. You wake up to find:
              </p>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm border dark:border-gray-600 mb-4">
                  <thead className="bg-gray-100 dark:bg-gray-700">
                    <tr>
                      <th className="p-3 text-left">Year</th>
                      <th className="p-3 text-right">TCS Price</th>
                      <th className="p-3 text-left">What Happened</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t dark:border-gray-600 bg-green-50 dark:bg-green-900/10">
                      <td className="p-3">2010</td>
                      <td className="p-3 text-right">₹550</td>
                      <td className="p-3">You bought</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600">
                      <td className="p-3">2013</td>
                      <td className="p-3 text-right">₹1,200</td>
                      <td className="p-3">Taper tantrum, market crashed 20%</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600">
                      <td className="p-3">2016</td>
                      <td className="p-3 text-right">₹2,400</td>
                      <td className="p-3">Demonetization panic</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600">
                      <td className="p-3">2020</td>
                      <td className="p-3 text-right">₹1,800</td>
                      <td className="p-3">COVID crash - fell 40%!</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600 bg-green-50 dark:bg-green-900/20">
                      <td className="p-3 font-bold">2024</td>
                      <td className="p-3 text-right font-bold">₹3,800</td>
                      <td className="p-3 font-bold text-green-600">You wake up: 590% gain + dividends!</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded">
                <p className="text-sm">
                  <strong>The Rip Van Winkle Advantage:</strong> By sleeping through all the panic, crashes, and volatility, 
                  you avoided the emotional trap of selling low. Long-term investors win because they ignore short-term noise!
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">The 3 Pillars of Long-term Success</h2>
              
              <div className="space-y-4">
                <div className="p-5 bg-green-50 dark:bg-green-900/20 rounded-lg border-2 border-green-500">
                  <h3 className="font-bold text-xl text-gray-800 dark:text-gray-100 mb-2">1. Buy Quality + Margin of Safety</h3>
                  <p className="text-sm mb-2">
                    Great companies (TCS, HDFC, Asian Paints) bought at good prices (20-30% below intrinsic value).
                  </p>
                  <p className="text-sm italic">Quality protects you in bad times. Cheap price gives you upside in good times.</p>
                </div>

                <div className="p-5 bg-blue-50 dark:bg-blue-900/20 rounded-lg border-2 border-blue-500">
                  <h3 className="font-bold text-xl text-gray-800 dark:text-gray-100 mb-2">2. Hold Through Volatility</h3>
                  <p className="text-sm mb-2">
                    Markets will crash. Your stocks will fall 30-50%. This is normal! If fundamentals haven't changed, HOLD.
                  </p>
                  <p className="text-sm italic">Short-term pain = Long-term gain. Selling in panic is the #1 reason people lose money.</p>
                </div>

                <div className="p-5 bg-purple-50 dark:bg-purple-900/20 rounded-lg border-2 border-purple-500">
                  <h3 className="font-bold text-xl text-gray-800 dark:text-gray-100 mb-2">3. Reinvest Dividends & Returns</h3>
                  <p className="text-sm mb-2">
                    When dividends come, reinvest them. When stocks go up, resist the urge to "book profit." Let compounding work!
                  </p>
                  <p className="text-sm italic">Every rupee reinvested becomes 10 rupees in 10 years, 100 rupees in 20 years (at 12%).</p>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">Common Mistakes That Kill Returns</h2>
              
              <div className="space-y-3">
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded border-l-4 border-red-500">
                  <p className="text-sm"><strong>❌ Buying without margin of safety:</strong> "Stock is good, I'll buy at any price!" - Recipe for disaster.</p>
                </div>
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded border-l-4 border-red-500">
                  <p className="text-sm"><strong>❌ Panic selling during crashes:</strong> Market falls 20% → You sell at loss → Market recovers → You miss the rebound.</p>
                </div>
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded border-l-4 border-red-500">
                  <p className="text-sm"><strong>❌ Frequent trading:</strong> Every trade = brokerage + taxes. 50 trades/year = 10-15% of returns gone!</p>
                </div>
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded border-l-4 border-red-500">
                  <p className="text-sm"><strong>❌ Chasing short-term gains:</strong> "I'll sell when it goes up 20%!" - You cut winners, keep losers. Do the opposite!</p>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">Your Action Plan</h2>
              
              <div className="p-5 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl">
                <ol className="space-y-3">
                  <li className="text-sm">
                    <strong>1. Identify 5-10 quality companies</strong> in India (high ROE, low debt, moats) that you understand.
                  </li>
                  <li className="text-sm">
                    <strong>2. Calculate intrinsic value</strong> for each (use our DCF calculator or P/E method).
                  </li>
                  <li className="text-sm">
                    <strong>3. Set buy prices</strong> at 20-30% below intrinsic value. Use alerts on Zerodha/Groww.
                  </li>
                  <li className="text-sm">
                    <strong>4. When opportunity comes</strong> (market crash, sector panic), BUY aggressively.
                  </li>
                  <li className="text-sm">
                    <strong>5. Hold for 10+ years.</strong> Ignore daily price movements. Check fundamentals yearly.
                  </li>
                  <li className="text-sm">
                    <strong>6. Sell only if:</strong> a) Fundamentals deteriorate badly, or b) You need money for emergency/goal.
                  </li>
                </ol>
              </div>
            </div>

            <div className="p-6 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl border border-indigo-200 dark:border-indigo-700">
              <h3 className="font-bold text-xl text-gray-800 dark:text-gray-100 mb-3">💡 Final Thoughts</h3>
              <p className="mb-3 text-sm">
                <em>"The stock market is a device for transferring money from the impatient to the patient."</em> - Warren Buffett
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Margin of safety:</strong> Your armor against errors, bad luck, and market crashes</li>
                <li><strong>Long-term thinking:</strong> Measured in decades, not days or months</li>
                <li><strong>Patience wins:</strong> The best investors do almost nothing - they just wait</li>
                <li><strong>Quality + Price:</strong> Both matter. Never compromise on either.</li>
                <li><strong>Ignore the noise:</strong> News, tips, opinions - all noise. Focus on fundamentals.</li>
              </ul>
              <p className="mt-4 text-sm font-semibold text-center">
                🎓 Congratulations! You've completed the entire learning journey. Now go build your wealth!
              </p>
            </div>

          </div>
        </Card>

        <div className="flex justify-between items-center">
          <Button 
            variant="outline" 
            onClick={() => navigate('/learn/buffett-philosophy')}
            className="dark:bg-gray-800 dark:text-gray-100"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous: Buffett's Philosophy
          </Button>
          <Button 
            onClick={() => navigate('/learn')}
            className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
          >
            🎉 Back to Learn Home
          </Button>
        </div>

      </div>
      <Footer />
    </div>
  );
}
